import type { Team } from '@compass/client/types/team';
import type { UserSummary } from '@compass/client/types/user';
import { useMemo } from 'react';

import { useTeamMembersMap } from '@/hooks/useTeams';

import type { Agent, CaseItem } from '../data/types';
import { ASSIGNED_TO_ME_ID } from '../data/types';

interface UseTeamAgentsParams {
  teams: Team[];
  /** View-model cases — `assigneeId` is already remapped for the current user
   *  to `'me'` by `WorkforceContainer`, and `riskLevel` carries the 3-tier
   *  classification. We use both to compute per-agent KPIs. */
  cases: CaseItem[];
  currentUserId: string | null;
  /** Authoritative self-availability from `getMyAvailability` — drives the
   *  online dot on the "Assigned to me" row. `lookupUsers` doesn't always
   *  surface self-availability so we accept it explicitly. */
  meAvailable: boolean;
}

interface CaseAggregates {
  open: number;
  breached: number;
  atRisk: number;
}

/** Sum per-assignee open / breached / at-risk counts from the view-model cases. */
function aggregateByAssignee(cases: CaseItem[]): Map<string, CaseAggregates> {
  const map = new Map<string, CaseAggregates>();
  for (const c of cases) {
    const key = c.assigneeId;
    if (!key) continue;
    let agg = map.get(key);
    if (!agg) {
      agg = { open: 0, breached: 0, atRisk: 0 };
      map.set(key, agg);
    }
    agg.open += 1;
    if (c.riskLevel === 'breached') agg.breached += 1;
    else if (c.riskLevel === 'warning') agg.atRisk += 1;
  }
  return map;
}

/** Collect uid → set-of-teams across all selected teams (manager + members). */
function buildMembershipMap(teams: Team[]): Map<string, Set<string>> {
  const userTeams = new Map<string, Set<string>>();
  for (const team of teams) {
    const teamId = team.id ?? '';
    if (!teamId) continue;
    const memberIds = new Set<string>(team.member_ids ?? []);
    if (team.manager_id) memberIds.add(team.manager_id);
    for (const uid of memberIds) {
      let set = userTeams.get(uid);
      if (!set) {
        set = new Set<string>();
        userTeams.set(uid, set);
      }
      set.add(teamId);
    }
  }
  return userTeams;
}

function buildMeAgent(
  agg: CaseAggregates,
  meAvailable: boolean,
  currentUserId: string | null,
  meUser: UserSummary | undefined,
): Agent {
  return {
    id: ASSIGNED_TO_ME_ID,
    name: 'Assigned to me',
    initial: 'M',
    openCount: agg.open,
    breachedCount: agg.breached,
    atRiskCount: agg.atRisk,
    online: meAvailable,
    teamIds: [],
    // Carry the real user's identity through to the avatar so initials +
    // deterministic color match the user's other appearances in the app.
    // The row label itself stays as "Assigned to me".
    avatarUserId: currentUserId ?? undefined,
    avatarName: meUser?.name || undefined,
    avatarEmail: meUser?.email || undefined,
  };
}

function buildMemberAgent(
  uid: string,
  teamIds: string[],
  user: UserSummary | undefined,
  agg: CaseAggregates,
): Agent {
  const name = user?.name || user?.email || 'Unknown';
  return {
    id: uid,
    name,
    initial: name.charAt(0).toUpperCase(),
    openCount: agg.open,
    breachedCount: agg.breached,
    atRiskCount: agg.atRisk,
    // `UserSummary.available` is populated by `lookupUsers` (used by
    // `useUsersByIds` / `useTeamMembersMap`) — server reads the user's
    // current availability TTL from the heartbeat store.
    online: user?.available ?? false,
    teamIds,
  };
}

/**
 * Builds the agent list rendered down the left edge of the Workforce Grid for
 * one or more selected teams.
 *
 *   - 1 synthetic "Assigned to me" row at the top — counts every case currently
 *     assigned to the logged-in user across all selected teams. The user does
 *     NOT also appear as a regular member row.
 *   - For every other user: exactly one row, merged across teams. `teamIds`
 *     captures the full membership so the reassign popover (in CaseCard) can
 *     scope eligibility per-case.
 *
 * KPI counts (`openCount`, `breachedCount`, `atRiskCount`) are computed from
 * the view-model `CaseItem.riskLevel`, so they reflect the 3-tier
 * classification (breached / warning / healthy) that the grid and KPI strip
 * also use.
 */
export const useTeamAgents = ({
  teams,
  cases,
  currentUserId,
  meAvailable,
}: UseTeamAgentsParams) => {
  const { usersMap, isLoading } = useTeamMembersMap(teams);

  const { agents, currentUserAgent } = useMemo(() => {
    if (teams.length === 0) return { agents: [] as Agent[], currentUserAgent: null };

    const aggMap = aggregateByAssignee(cases);
    const userTeams = buildMembershipMap(teams);

    const empty: CaseAggregates = { open: 0, breached: 0, atRisk: 0 };

    // Build a "real" agent row for the current user (with their actual
    // user_id, name, email) BEFORE dropping them from the regular member
    // rows. The grid doesn't render this row — cases assigned to the current
    // user are remapped to the synthetic `'me'` row by the container — but
    // the reassign dropdowns need a valid user_id target so the manager can
    // (re)assign cases to themselves. Aggregates come from `aggMap` keyed on
    // the real user id, which is always 0 in practice (those cases are
    // re-keyed to `'me'`); we still pass `empty` rather than reading from
    // `aggMap` so future call sites don't accidentally double-count.
    let currentUserAgentLocal: Agent | null = null;
    if (currentUserId) {
      const teamIdSet = userTeams.get(currentUserId);
      if (teamIdSet) {
        currentUserAgentLocal = buildMemberAgent(
          currentUserId,
          [...teamIdSet],
          usersMap.get(currentUserId),
          empty,
        );
      }
      // Skip the current user from regular member rows — their cases live in
      // the "Assigned to me" row at the top of the grid.
      userTeams.delete(currentUserId);
    }

    const memberAgents: Agent[] = [];
    for (const [uid, teamIdSet] of userTeams) {
      memberAgents.push(
        buildMemberAgent(uid, [...teamIdSet], usersMap.get(uid), aggMap.get(uid) ?? empty),
      );
    }

    const meAgg = aggMap.get(ASSIGNED_TO_ME_ID) ?? empty;
    const meUser = currentUserId ? usersMap.get(currentUserId) : undefined;
    const meAgent = buildMeAgent(meAgg, meAvailable, currentUserId, meUser);

    return { agents: [meAgent, ...memberAgents], currentUserAgent: currentUserAgentLocal };
  }, [teams, cases, usersMap, currentUserId, meAvailable]);

  return { agents, currentUserAgent, isLoading };
};
